// Layout Style Plugin
import { createSimpleProperty } from './helpers.js';

export default {
    id: 'layout',
    label: 'Layout',
    icon: 'Layout',
    properties: {
        aspectRatio: {
            label: 'Aspect Ratio',
            type: 'select',
            options: ['auto', 'square', 'video'],
            default: 'auto',
            ...createSimpleProperty('aspect')
        },
        columns: {
            label: 'Columns',
            type: 'select',
            options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', 'auto', '3xs', '2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl'],
            default: 'auto',
            ...createSimpleProperty('columns')
        },
        breakAfter: {
            label: 'Break After',
            type: 'select',
            options: ['auto', 'avoid', 'all', 'avoid-page', 'page', 'left', 'right', 'column'],
            default: 'auto',
            ...createSimpleProperty('break-after')
        },
        breakBefore: {
            label: 'Break Before',
            type: 'select',
            options: ['auto', 'avoid', 'all', 'avoid-page', 'page', 'left', 'right', 'column'],
            default: 'auto',
            ...createSimpleProperty('break-before')
        },
        breakInside: {
            label: 'Break Inside',
            type: 'select',
            options: ['auto', 'avoid', 'avoid-page', 'avoid-column'],
            default: 'auto',
            ...createSimpleProperty('break-inside')
        },
        boxDecorationBreak: {
            label: 'Box Decoration Break',
            type: 'select',
            options: ['clone', 'slice'],
            default: 'slice',
            ...createSimpleProperty('box-decoration')
        },
        boxSizing: {
            label: 'Box Sizing',
            type: 'select',
            options: ['border', 'content'],
            default: 'border',
            toClass: (value) => value ? `box-${value}` : null,
            fromClass: (className) => {
                const match = className.match(/^box-(border|content)$/);
                return match ? match[1] : null;
            }
        },
        display: {
            label: 'Display',
            type: 'select',
            options: ['block', 'inline-block', 'inline', 'flex', 'inline-flex', 'table', 'inline-table', 'table-caption', 'table-cell', 'table-column', 'table-column-group', 'table-footer-group', 'table-header-group', 'table-row-group', 'table-row', 'flow-root', 'grid', 'inline-grid', 'contents', 'list-item', 'hidden'],
            default: 'block',
            toClass: (value) => value || null,
            fromClass: (className) => {
                const displays = ['block', 'inline-block', 'inline', 'flex', 'inline-flex', 'table', 'inline-table', 'table-caption', 'table-cell', 'table-column', 'table-column-group', 'table-footer-group', 'table-header-group', 'table-row-group', 'table-row', 'flow-root', 'grid', 'inline-grid', 'contents', 'list-item', 'hidden'];
                return displays.includes(className) ? className : null;
            }
        },
        float: {
            label: 'Float',
            type: 'select',
            options: ['start', 'end', 'right', 'left', 'none'],
            default: 'none',
            ...createSimpleProperty('float')
        },
        clear: {
            label: 'Clear',
            type: 'select',
            options: ['start', 'end', 'left', 'right', 'both', 'none'],
            default: 'none',
            ...createSimpleProperty('clear')
        },
        isolation: {
            label: 'Isolation',
            type: 'select',
            options: ['isolate', 'isolation-auto'],
            default: 'isolation-auto',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (['isolate', 'isolation-auto'].includes(className)) {
                    return className;
                }
                return null;
            }
        },
        objectFit: {
            label: 'Object Fit',
            type: 'select',
            options: ['contain', 'cover', 'fill', 'none', 'scale-down'],
            default: 'none',
            toClass: (value) => value ? `object-${value}` : null,
            fromClass: (className) => {
                const match = className.match(/^object-(contain|cover|fill|none|scale-down)$/);
                return match ? match[1] : null;
            }
        },
        objectPosition: {
            label: 'Object Position',
            type: 'select',
            options: ['bottom', 'center', 'left', 'left-bottom', 'left-top', 'right', 'right-bottom', 'right-top', 'top'],
            default: 'center',
            toClass: (value) => value ? `object-${value}` : null,
            fromClass: (className) => {
                const match = className.match(/^object-(bottom|center|left|left-bottom|left-top|right|right-bottom|right-top|top)$/);
                return match ? match[1] : null;
            }
        },
        overflow: {
            label: 'Overflow',
            type: 'select',
            options: ['auto', 'hidden', 'clip', 'visible', 'scroll'],
            default: 'visible',
            ...createSimpleProperty('overflow')
        },
        overscrollBehavior: {
            label: 'Overscroll Behavior',
            type: 'select',
            options: ['auto', 'contain', 'none'],
            default: 'auto',
            ...createSimpleProperty('overscroll')
        },
        position: {
            label: 'Position',
            type: 'select',
            options: ['static', 'fixed', 'absolute', 'relative', 'sticky'],
            default: 'static',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (['static', 'fixed', 'absolute', 'relative', 'sticky'].includes(className)) {
                    return className;
                }
                return null;
            }
        },
        visibility: {
            label: 'Visibility',
            type: 'select',
            options: ['visible', 'invisible', 'collapse'],
            default: 'visible',
            toClass: (value) => value || null,
            fromClass: (className) => {
                if (['visible', 'invisible', 'collapse'].includes(className)) {
                    return className;
                }
                return null;
            }
        },
        zIndex: {
            label: 'Z-Index',
            type: 'select',
            options: ['0', '10', '20', '30', '40', '50', 'auto'],
            default: 'auto',
            ...createSimpleProperty('z')
        },
        inset: {
            label: 'Inset',
            type: 'select',
            options: ['0', 'px', 'auto', 'full'],
            default: 'auto',
            ...createSimpleProperty('inset')
        },
        insetX: {
            label: 'Inset X',
            type: 'select',
            options: ['0', 'px', 'auto', 'full'],
            default: 'auto',
            ...createSimpleProperty('inset-x')
        },
        insetY: {
            label: 'Inset Y',
            type: 'select',
            options: ['0', 'px', 'auto', 'full'],
            default: 'auto',
            ...createSimpleProperty('inset-y')
        },
        top: {
            label: 'Top',
            type: 'select',
            options: ['0', 'px', '0.5', '1', '2', '3', '4', '5', '6', '8', '10', '12', '16', '20', '24', '32', '40', '48', '56', '64', 'auto', 'full', '1/2', '1/3', '2/3', '1/4', '3/4'],
            default: 'auto',
            ...createSimpleProperty('top')
        },
        right: {
            label: 'Right',
            type: 'select',
            options: ['0', 'px', '0.5', '1', '2', '3', '4', '5', '6', '8', '10', '12', '16', '20', '24', '32', '40', '48', '56', '64', 'auto', 'full', '1/2', '1/3', '2/3', '1/4', '3/4'],
            default: 'auto',
            ...createSimpleProperty('right')
        },
        bottom: {
            label: 'Bottom',
            type: 'select',
            options: ['0', 'px', '0.5', '1', '2', '3', '4', '5', '6', '8', '10', '12', '16', '20', '24', '32', '40', '48', '56', '64', 'auto', 'full', '1/2', '1/3', '2/3', '1/4', '3/4'],
            default: 'auto',
            ...createSimpleProperty('bottom')
        },
        left: {
            label: 'Left',
            type: 'select',
            options: ['0', 'px', '0.5', '1', '2', '3', '4', '5', '6', '8', '10', '12', '16', '20', '24', '32', '40', '48', '56', '64', 'auto', 'full', '1/2', '1/3', '2/3', '1/4', '3/4'],
            default: 'auto',
            ...createSimpleProperty('left')
        }
    }
};
